import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine } from '../contexts/BusinessLineContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency } from '../lib/utils';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Target,
  DollarSign,
  TrendingUp,
  Megaphone,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  PieChart as PieChartIcon,
  BarChart2,
  LayoutGrid,
  Trash2,
  Building2,
  ShieldCheck,
  Award,
  Home,
  Star,
  GripVertical,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Trophy,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oculta la card completa de Ingresos (admin) sin tocar el fetch/estado
// (ingresosData, ingresosYear, ingresosAseguradora, ingresosYears).
const SHOW_INGRESOS_CARD = false;

const CHART_COLORS = ['#1a365d', '#2d8659', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const INSIGNIA_MEDAL_CONFIG = {
  bronce:        { color: '#CD7F32', borderColor: '#CD7F32', fillColor: '#CD7F3220', initial: 'B',  textColor: '#fff', label: 'Bronce',        text: '¡Buen inicio! Sigue creciendo tu cartera.' },
  plata:         { color: '#A8A9AD', borderColor: '#A8A9AD', fillColor: '#A8A9AD20', initial: 'P',  textColor: '#333', label: 'Plata',         text: '¡Vas avanzando! El oro está en tu camino.' },
  oro:           { color: '#FFD700', borderColor: '#FFD700', fillColor: '#FFD70020', initial: 'O',  textColor: '#333', label: 'Oro',           text: '¡Excelente producción! Apunta al Paladio.' },
  paladio:       { color: '#A0522D', borderColor: '#A0522D', fillColor: '#A0522D20', initial: 'Pa', textColor: '#fff', label: 'Paladio',       text: '¡Gran nivel! El Platino te espera.' },
  platino:       { color: '#6E6E6E', borderColor: '#6E6E6E', fillColor: '#6E6E6E20', initial: 'Pl', textColor: '#fff', label: 'Platino',       text: '¡Muy cerca de la cima! Un último esfuerzo.' },
  insignia_life: { color: '#DC143C', borderColor: '#DC143C', fillColor: '#DC143C20', initial: 'IL', textColor: '#fff', label: 'Insignia Life', text: '¡Nivel máximo alcanzado! Eres experto Insignia Life.' },
};

const _MEDAL_DEFS = {
  bronce:        { c1: '#CD7F32', c2: '#8B4513', border: '#6B3410', text: 'B'  },
  plata:         { c1: '#C0C0C0', c2: '#808080', border: '#606060', text: 'P'  },
  oro:           { c1: '#FFD700', c2: '#B8860B', border: '#8B6914', text: 'O'  },
  paladio:       { c1: '#C47E5A', c2: '#6B3A2A', border: '#4A2518', text: 'Pa' },
  platino:       { c1: '#E5E4E2', c2: '#4A4A4A', border: '#333333', text: 'Pl' },
  insignia_life: { c1: '#DC143C', c2: '#8B0000', border: '#5C0000', text: 'IL' },
};
const InsigniaMedalSVG = ({ nivel, size = 28, caducada = false }) => {
  const d = _MEDAL_DEFS[nivel] || { c1: '#888', c2: '#555', border: '#333', text: '?' };
  const gid = `igm_${nivel || 'none'}`;
  const sw = Math.max(2, size * 0.1);
  const r = size / 2 - sw / 2;
  const cx = size / 2;
  const cy = size / 2;
  const fs = d.text.length > 1 ? size * 0.27 : size * 0.35;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" style={{ flexShrink: 0, ...(caducada ? { filter: 'grayscale(100%)', opacity: 0.4 } : {}) }}>
      <defs>
        <radialGradient id={gid} cx="40%" cy="35%" r="65%" fx="40%" fy="35%">
          <stop offset="0%" stopColor={d.c1} />
          <stop offset="100%" stopColor={d.c2} />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill={`url(#${gid})`} stroke={d.border} strokeWidth={sw} />
      <ellipse cx={cx * 0.78} cy={cy * 0.62} rx={r * 0.35} ry={r * 0.2} fill="rgba(255,255,255,0.28)" />
      <text x={cx} y={cy + fs * 0.38} textAnchor="middle" fontSize={fs} fill="white" fontWeight="bold" fontFamily="system-ui, sans-serif">{d.text}</text>
      {size >= 40 && <text x={cx} y={cy + r * 0.75} textAnchor="middle" fontSize={size * 0.14} fill="rgba(255,255,255,0.55)">★</text>}
    </svg>
  );
};

const INSIGNIA_MARKERS = [
  { nivel: 'bronce',        prima: 9_000,   color: '#CD7F32' },
  { nivel: 'plata',         prima: 45_000,  color: '#A8A9AD' },
  { nivel: 'oro',           prima: 70_000,  color: '#FFD700' },
  { nivel: 'paladio',       prima: 160_000, color: '#A0522D' },
  { nivel: 'platino',       prima: 260_000, color: '#6E6E6E' },
  { nivel: 'insignia_life', prima: 400_000, color: '#DC143C' },
];
const _INSIGNIA_NIVEL_ORDER = ['bronce', 'plata', 'oro', 'paladio', 'platino', 'insignia_life'];

const InsigniaProgressWithMarkers = ({ currentPrima, meta, barColor, currentNivel, medalasStatus = {} }) => {
  const pct = Math.min(100, (currentPrima / meta) * 100);
  const currentIdx = currentNivel ? _INSIGNIA_NIVEL_ORDER.indexOf(currentNivel) : -1;
  return (
    <div className="relative" style={{ paddingTop: '32px', paddingBottom: '4px', marginTop: '4px' }}>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
      </div>
      {INSIGNIA_MARKERS.map(m => {
        const mPct = (m.prima / meta) * 100;
        if (mPct > 100) return null;
        const isActive = _INSIGNIA_NIVEL_ORDER.indexOf(m.nivel) <= currentIdx;
        const isCaducada = !isActive && medalasStatus[m.nivel]?.status === 'caducada';
        const medalOpacity = isActive ? 1 : isCaducada ? 0.5 : 0.22;
        const tickColor = isCaducada ? '#9CA3AF' : m.color;
        const tickOpacity = isActive ? 1 : isCaducada ? 0.45 : 0.3;
        return (
          <div key={m.nivel} className="absolute" style={{ left: `${mPct}%`, top: 0, transform: 'translateX(-50%)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ opacity: medalOpacity }}>
              <InsigniaMedalSVG nivel={m.nivel} size={28} caducada={isCaducada} />
            </div>
            <div style={{ width: 2, height: 12, backgroundColor: tickColor, borderRadius: 1, boxShadow: isActive ? `0 0 4px ${m.color}` : 'none', opacity: tickOpacity }} />
          </div>
        );
      })}
    </div>
  );
};

const normalizeStatus = (status) => {
  const map = {
    'nuevo': 'Nuevo',
    'contactado': 'Contactado',
    'en_negociacion': 'En Negociación',
    'pendiente_de_pago': 'Pendiente de Pago',
    'ganado': 'Ganado',
    'perdido': 'Perdido',
  };
  return map[status] || status;
};

const normalizeSource = (source) => {
  const map = {
    'manual': 'Manual',
    'reporte_ventas': 'Reporte de Ventas',
    'instagram': 'Instagram',
    'instagram dms': 'Instagram DMs',
    'facebook': 'Facebook',
    'facebook messenger': 'Facebook Messenger',
    'whatsapp': 'WhatsApp',
    'llamada_fria': 'Llamada en Frío',
    'llamada en frío': 'Llamada en Frío',
    'referido': 'Referido',
    'n8n': null,
  };
  return map[source?.toLowerCase()] ?? source;
};

const ASEGURADORAS = [
  { label: 'Allianz', value: 'allianz' },
  { label: 'Qualitas', value: 'qualitas' },
  { label: 'Insignia Life', value: 'insignia' },
  { label: 'Soluciones Inmobiliarias', value: 'soluciones_inmobiliarias' },
];

// ─── Preset catalogue ────────────────────────────────────────────────
const PRESET_CATALOGUE = [
  {
    id: 'leads_por_agente',
    title: 'Leads por Agente',
    description: 'Distribución de leads asignados por agente y estado',
    icon: Target,
    endpoint: '/dashboard/leads-por-agente',
    defaultChart: 'bar',
    supportsAseguradora: true,
  },
  {
    id: 'clientes_por_agente',
    title: 'Clientes por Agente',
    description: 'Clientes activos agrupados por agente asignado',
    icon: Users,
    endpoint: '/dashboard/clientes-por-agente',
    defaultChart: 'bar',
    supportsAseguradora: true,
  },
  {
    id: 'polizas_por_agente',
    title: 'Pólizas por Agente',
    description: 'Pólizas emitidas con desglose de estados y prima total',
    icon: ShieldCheck,
    endpoint: '/dashboard/polizas-por-agente',
    defaultChart: 'bar',
    supportsAseguradora: true,
  },
  {
    id: 'polizas_general',
    title: 'Pólizas General',
    description: 'KPIs globales de pólizas y distribución por línea de negocio',
    icon: ShieldCheck,
    endpoint: '/dashboard/polizas-general',
    defaultChart: 'pie',
    supportsAseguradora: true,
  },
  {
    id: 'campanas',
    title: 'Campañas Meta Ads',
    description: 'Inversión, impresiones, clics y leads por agente en Meta Ads',
    icon: Megaphone,
    endpoint: '/dashboard/campanas',
    defaultChart: 'bar',
    supportsAseguradora: false,
  },
  {
    id: 'clientes_general',
    title: 'Clientes General',
    description: 'KPIs globales de clientes, fuentes de captación y prima total',
    icon: Users,
    endpoint: '/dashboard/clientes-general',
    defaultChart: 'pie',
    supportsAseguradora: true,
  },
  {
    id: 'agentes_rendimiento',
    title: 'Rendimiento de Agentes',
    description: 'Tabla comparativa de agentes: leads, clientes, pólizas y conversión',
    icon: Award,
    endpoint: '/dashboard/agentes-rendimiento',
    defaultChart: 'table',
    supportsAseguradora: true,
  },
  {
    id: 'lineas_negocio',
    title: 'Líneas de Negocio',
    description: 'Distribución de leads y pólizas por línea de negocio / producto',
    icon: Building2,
    endpoint: '/dashboard/lineas-negocio',
    defaultChart: 'bar',
    supportsAseguradora: false,
  },
  {
    id: 'soluciones_inmobiliarias',
    title: 'Soluciones Inmobiliarias',
    description: 'Estado de leads inmobiliarios captados en el módulo de inmuebles',
    icon: Home,
    endpoint: '/dashboard/soluciones-inmobiliarias',
    defaultChart: 'pie',
    supportsAseguradora: false,
  },
];

// ─── Stat card ───────────────────────────────────────────────────────
const StatCard = ({ title, value, description, icon: Icon, trend, trendUp, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-[#1a365d] text-white',
    secondary: 'bg-[#2d8659] text-white',
    accent: 'bg-[#6b7280] text-white',
    muted: 'bg-muted text-muted-foreground',
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value}</p>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            {trend && (
              <div className={`flex items-center gap-1 text-xs ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
                {trendUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                <span>{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Chart type toggle ────────────────────────────────────────────────
const ChartToggle = ({ value, onChange, options }) => (
  <div className="flex gap-1">
    {options.map(opt => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className={`p-1.5 rounded text-xs flex items-center gap-1 transition-colors ${
          value === opt.value
            ? 'bg-[#1a365d] text-white'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
        }`}
        title={opt.label}
      >
        <opt.icon className="h-3.5 w-3.5" />
      </button>
    ))}
  </div>
);

// ─── Grouped filter selector (collapsible Popover) ───────────────────
const PresetFilters = ({ filters, onChange, agentesLista, supportsAseguradora }) => {
  const [open, setOpen] = useState(false);
  const [aseguradoraOpen, setAseguradoraOpen] = useState(false);
  const [agenteOpen, setAgenteOpen] = useState(false);

  const getDisplayLabel = () => {
    if (filters.agente_id) {
      const agent = agentesLista.find(a => a.user_id === filters.agente_id);
      return agent ? agent.display_name : 'Agente';
    }
    if (filters.aseguradora) {
      const aseg = ASEGURADORAS.find(a => a.value === filters.aseguradora);
      return aseg ? aseg.label : 'Aseguradora';
    }
    return 'Todos';
  };

  const handleSelect = (val) => {
    if (val === '__all__') {
      onChange({ ...filters, agente_id: '', aseguradora: '' });
    } else if (val.startsWith('agente:')) {
      onChange({ ...filters, agente_id: val.slice(7), aseguradora: '' });
    } else if (val.startsWith('aseguradora:')) {
      onChange({ ...filters, agente_id: '', aseguradora: val.slice(12) });
    }
    setOpen(false);
  };

  const isSelected = (val) => {
    if (val === '__all__') return !filters.agente_id && !filters.aseguradora;
    if (val.startsWith('agente:')) return filters.agente_id === val.slice(7);
    if (val.startsWith('aseguradora:')) return filters.aseguradora === val.slice(12);
    return false;
  };

  const sortedAgentes = [...agentesLista].sort((a, b) =>
    a.display_name.localeCompare(b.display_name, 'es', { sensitivity: 'base' })
  );

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-7 text-xs px-2 w-48 border rounded-md flex items-center justify-between gap-1 bg-background hover:bg-muted transition-colors text-left"
          >
            <span className="truncate">{getDisplayLabel()}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-52 p-1" sideOffset={4}>
          {/* Todos */}
          <button
            type="button"
            onClick={() => handleSelect('__all__')}
            className={`w-full text-left text-xs px-3 py-1.5 rounded hover:bg-muted transition-colors ${
              isSelected('__all__') ? 'bg-muted font-medium' : ''
            }`}
          >
            Todos
          </button>

          {/* Por Aseguradora */}
          {supportsAseguradora && (
            <div>
              <button
                type="button"
                onClick={() => setAseguradoraOpen(v => !v)}
                className="w-full flex items-center justify-between text-xs font-semibold text-muted-foreground px-3 py-1.5 hover:bg-muted/50 rounded mt-1 transition-colors"
              >
                Por Aseguradora
                <ChevronRight className={`h-3 w-3 transition-transform duration-150 ${aseguradoraOpen ? 'rotate-90' : ''}`} />
              </button>
              {aseguradoraOpen && (
                <div className="pl-3">
                  {ASEGURADORAS.map(a => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => handleSelect(`aseguradora:${a.value}`)}
                      className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted transition-colors ${
                        isSelected(`aseguradora:${a.value}`) ? 'bg-muted font-medium' : ''
                      }`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Por Agente */}
          {sortedAgentes.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setAgenteOpen(v => !v)}
                className="w-full flex items-center justify-between text-xs font-semibold text-muted-foreground px-3 py-1.5 hover:bg-muted/50 rounded mt-1 transition-colors"
              >
                Por Agente
                <ChevronRight className={`h-3 w-3 transition-transform duration-150 ${agenteOpen ? 'rotate-90' : ''}`} />
              </button>
              {agenteOpen && (
                <div className="pl-3 max-h-48 overflow-y-auto">
                  {sortedAgentes.map(a => (
                    <button
                      key={a.user_id}
                      type="button"
                      onClick={() => handleSelect(`agente:${a.user_id}`)}
                      className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted transition-colors ${
                        isSelected(`agente:${a.user_id}`) ? 'bg-muted font-medium' : ''
                      }`}
                    >
                      {a.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Input
        type="date"
        className="h-7 text-xs w-36"
        value={filters.fecha_inicio || ''}
        onChange={e => onChange({ ...filters, fecha_inicio: e.target.value })}
      />
      <Input
        type="date"
        className="h-7 text-xs w-36"
        value={filters.fecha_fin || ''}
        onChange={e => onChange({ ...filters, fecha_fin: e.target.value })}
      />
    </div>
  );
};

// ─── Preset dashboard card (sortable) ────────────────────────────────
const PresetDashboardCard = ({ presetId, dashboardDbId, onRemove, agentesLista, externalAseguradora }) => {
  const meta = PRESET_CATALOGUE.find(p => p.id === presetId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState(meta?.defaultChart || 'bar');
  const [filters, setFilters] = useState({ agente_id: '', aseguradora: externalAseguradora || '', fecha_inicio: '', fecha_fin: '' });

  // Sync with global business line selector
  useEffect(() => {
    if (externalAseguradora !== undefined) {
      setFilters(prev => ({ ...prev, aseguradora: externalAseguradora }));
    }
  }, [externalAseguradora]);

  // dnd-kit sortable — IDs as strings for type safety
  const sortableId = String(dashboardDbId);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortableId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  const fetchData = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.agente_id) params.agente_id = filters.agente_id;
      if (filters.aseguradora) params.aseguradora = filters.aseguradora;
      if (filters.fecha_inicio) params.fecha_inicio = filters.fecha_inicio;
      if (filters.fecha_fin) params.fecha_fin = filters.fecha_fin;
      const res = await axios.get(`${API}${meta.endpoint}`, { params });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching preset dashboard:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [meta, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!meta) return null;
  const Icon = meta.icon;

  const renderChart = () => {
    if (loading) return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
      </div>
    );
    if (!data) return (
      <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
    );

    // ── leads_por_agente / clientes_por_agente / polizas_por_agente ──
    if (['leads_por_agente', 'clientes_por_agente', 'polizas_por_agente'].includes(presetId)) {
      const rows = Array.isArray(data) ? data.slice(0, 10) : [];
      if (rows.length === 0) return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>;

      const barKeys = presetId === 'leads_por_agente'
        ? ['nuevo', 'contactado', 'en_negociacion', 'ganado', 'perdido']
        : presetId === 'clientes_por_agente'
        ? ['activo', 'inactivo', 'prospecto']
        : ['active', 'pending', 'pending_payment', 'expired', 'cancelled'];
      const barColors = ['#1a365d', '#2d8659', '#f59e0b', '#06b6d4', '#ef4444'];

      if (chartType === 'pie') {
        const pieData = rows.map((r, i) => ({ name: r.agent_name, value: r.total, fill: CHART_COLORS[i % CHART_COLORS.length] }));
        return (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="agent_name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {barKeys.map((k, i) => (
              <Bar key={k} dataKey={k} stackId="a" fill={barColors[i % barColors.length]}
                radius={i === barKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── polizas_general ──
    if (presetId === 'polizas_general') {
      const statusData = [
        { name: 'Activas', value: data.active || 0, fill: '#2d8659' },
        { name: 'Pendiente pago', value: data.pending_payment || 0, fill: '#f59e0b' },
        { name: 'Pendientes', value: data.pending || 0, fill: '#06b6d4' },
        { name: 'Vencidas', value: data.expired || 0, fill: '#ef4444' },
        { name: 'Canceladas', value: data.cancelled || 0, fill: '#6b7280' },
      ].filter(d => d.value > 0);
      const kpis = [
        { label: 'Total pólizas', value: data.total || 0 },
        { label: 'Prima total', value: formatCurrency(data.total_premium || 0) },
      ];
      if (chartType === 'bar') {
        const blData = (data.by_business_line || []).slice(0, 8);
        return (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {kpis.map(k => (
                <div key={k.label} className="bg-muted/40 rounded-lg p-2 text-center">
                  <p className="text-xs text-muted-foreground">{k.label}</p>
                  <p className="font-bold text-sm">{k.value}</p>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={blData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="line" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1a365d" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        );
      }
      return (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {kpis.map(k => (
              <div key={k.label} className="bg-muted/40 rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">{k.label}</p>
                <p className="font-bold text-sm">{k.value}</p>
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </>
      );
    }

    // ── campanas ──
    if (presetId === 'campanas') {
      const rows = Array.isArray(data) ? data.slice(0, 10) : [];
      if (rows.length === 0) return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Sin campañas registradas</div>;
      if (chartType === 'table') {
        return (
          <div className="max-h-52 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Agente</TableHead>
                  <TableHead className="text-xs text-right">Camp.</TableHead>
                  <TableHead className="text-xs text-right">Gasto</TableHead>
                  <TableHead className="text-xs text-right">Leads</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-medium">{r.agent_name}</TableCell>
                    <TableCell className="text-xs text-right">{r.campaigns_count}</TableCell>
                    <TableCell className="text-xs text-right">{formatCurrency(r.total_spend)}</TableCell>
                    <TableCell className="text-xs text-right">{r.total_leads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="agent_name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="total_leads" name="Leads" fill="#1a365d" radius={[4, 4, 0, 0]} />
            <Bar dataKey="total_clicks" name="Clics" fill="#2d8659" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── clientes_general ──
    if (presetId === 'clientes_general') {
      const kpis = [
        { label: 'Total clientes', value: data.total || 0 },
        { label: 'Activos', value: data.activo || 0 },
        { label: 'Inactivos', value: data.inactivo || 0 },
        { label: 'Prima total', value: formatCurrency(data.total_premium || 0) },
      ];
      const srcData = (data.by_source || []).slice(0, 6).map((s, i) => ({ ...s, fill: CHART_COLORS[i % CHART_COLORS.length] }));
      return (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {kpis.map(k => (
              <div key={k.label} className="bg-muted/40 rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">{k.label}</p>
                <p className="font-bold text-sm">{k.value}</p>
              </div>
            ))}
          </div>
          {chartType === 'bar' ? (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={srcData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="source" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" name="Clientes" fill="#2d8659" radius={[4, 4, 0, 0]}>
                  {srcData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={srcData} cx="50%" cy="50%" outerRadius={60} dataKey="count" nameKey="source" label={({ source, count }) => `${source}: ${count}`}>
                  {srcData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </>
      );
    }

    // ── agentes_rendimiento ──
    if (presetId === 'agentes_rendimiento') {
      const rows = Array.isArray(data) ? data : [];
      return (
        <div className="max-h-56 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Agente</TableHead>
                <TableHead className="text-xs text-right">Leads</TableHead>
                <TableHead className="text-xs text-right">Ganados</TableHead>
                <TableHead className="text-xs text-right">Clientes</TableHead>
                <TableHead className="text-xs text-right">Pólizas</TableHead>
                <TableHead className="text-xs text-right">Conv.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs font-medium">{r.agent_name}</TableCell>
                  <TableCell className="text-xs text-right">{r.leads_total}</TableCell>
                  <TableCell className="text-xs text-right">{r.leads_ganado}</TableCell>
                  <TableCell className="text-xs text-right">{r.clientes_total}</TableCell>
                  <TableCell className="text-xs text-right">{r.polizas_total}</TableCell>
                  <TableCell className="text-xs text-right">
                    <Badge variant={r.conversion_rate >= 20 ? 'default' : 'secondary'} className="text-xs px-1">
                      {r.conversion_rate}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }

    // ── lineas_negocio ──
    if (presetId === 'lineas_negocio') {
      const rows = Array.isArray(data) ? data.slice(0, 10) : [];
      if (rows.length === 0) return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>;
      if (chartType === 'pie') {
        const pieData = rows.map((r, i) => ({ name: r.line, value: r.leads_count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
        return (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="line" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="leads_count" name="Leads" fill="#1a365d" radius={[4, 4, 0, 0]} />
            <Bar dataKey="policies_count" name="Pólizas" fill="#2d8659" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── soluciones_inmobiliarias ──
    if (presetId === 'soluciones_inmobiliarias') {
      const statusColors = {
        no_contactado: '#6b7280', contactado: '#2d8659', interesado: '#f59e0b',
        no_interesado: '#ef4444', en_proceso: '#8b5cf6', cerrado: '#1a365d',
      };
      const byStatus = (data.by_status || []).map(s => ({ ...s, fill: statusColors[s.status] || '#06b6d4' }));
      if (chartType === 'bar') {
        return (
          <>
            <div className="bg-muted/40 rounded-lg p-2 text-center mb-3">
              <p className="text-xs text-muted-foreground">Total leads inmobiliarios</p>
              <p className="font-bold text-lg">{data.total || 0}</p>
            </div>
            <ResponsiveContainer width="100%" height={170}>
              <BarChart data={byStatus} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="status" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1a365d" radius={[4, 4, 0, 0]}>
                  {byStatus.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        );
      }
      return (
        <>
          <div className="bg-muted/40 rounded-lg p-2 text-center mb-3">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="font-bold text-lg">{data.total || 0}</p>
          </div>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={byStatus} cx="50%" cy="50%" outerRadius={70} dataKey="count" nameKey="status" label={({ status, count }) => `${status}: ${count}`}>
                {byStatus.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </>
      );
    }

    return <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Sin vista disponible</div>;
  };

  const toggleOptions = presetId === 'agentes_rendimiento'
    ? []
    : presetId === 'campanas'
    ? [{ value: 'bar', label: 'Barras', icon: BarChart2 }, { value: 'table', label: 'Tabla', icon: LayoutGrid }]
    : [{ value: 'bar', label: 'Barras', icon: BarChart2 }, { value: 'pie', label: 'Circular', icon: PieChartIcon }];

  return (
    // listeners en el wrapper completo — toda la tarjeta es arrastrable
    <div
      ref={setNodeRef}
      style={{ ...style, touchAction: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
      {...attributes}
      {...listeners}
    >
      <Card className="h-full" style={{ cursor: 'inherit' }}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              {/* GripVertical — solo indicador visual, sin listeners propios */}
              <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <CardTitle className="text-base truncate">{meta.title}</CardTitle>
            </div>
            {/* Zona interactiva — se excluye del drag */}
            <div
              className="flex items-center gap-1 shrink-0"
              onPointerDown={e => e.stopPropagation()}
            >
              {toggleOptions.length > 0 && (
                <ChartToggle value={chartType} onChange={setChartType} options={toggleOptions} />
              )}
              <button
                type="button"
                onClick={() => onRemove(dashboardDbId)}
                className="p-1.5 rounded text-muted-foreground hover:text-red-600 hover:bg-muted transition-colors"
                title="Eliminar dashboard"
                style={{ cursor: 'pointer' }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {/* Filtros — se excluyen del drag */}
          <div onPointerDown={e => e.stopPropagation()}>
            <PresetFilters
              filters={filters}
              onChange={setFilters}
              agentesLista={agentesLista}
              supportsAseguradora={meta.supportsAseguradora}
            />
          </div>
        </CardHeader>
        {/* Área de gráficos — se excluye del drag */}
        <CardContent className="pt-0" onPointerDown={e => e.stopPropagation()}>
          {renderChart()}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Mi Dashboard (non-admin) ─────────────────────────────────────────
const MiDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dashboard/mi-resumen`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="h-32 flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
    </div>
  );
  if (!data) return null;

  const kpis = [
    { label: 'Mis Leads', value: data.leads_total, icon: Target, color: 'primary' },
    { label: 'Leads Ganados', value: data.leads_ganado, icon: TrendingUp, color: 'secondary' },
    { label: 'Mis Clientes', value: data.clientes_total, icon: Users, color: 'primary' },
    { label: 'Mis Pólizas', value: data.polizas_total, icon: ShieldCheck, color: 'secondary' },
    { label: 'Prima Total', value: formatCurrency(data.premium_total), icon: DollarSign, color: 'accent' },
    { label: 'Conversión', value: `${data.conversion_rate}%`, icon: Star, color: 'accent' },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Mi Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map(k => (
          <StatCard key={k.label} title={k.label} value={k.value} icon={k.icon} color={k.color} />
        ))}
      </div>
      {data.recent_leads?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mis últimos leads</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fuente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_leads.map(l => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{l.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{l.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Gallery modal ─────────────────────────────────────────────────────
const GalleryModal = ({ open, onClose, activePresets, onAdd }) => {
  const handleRequest = async () => {
    try {
      await axios.post(`${API}/notifications`, {
        type: 'system',
        title: 'Solicitud de dashboard personalizado',
        message: 'Un usuario ha solicitado un dashboard personalizado.',
        is_read: false,
      });
      toast.success('Solicitud enviada al administrador');
    } catch {
      toast.info('Solicitud registrada');
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Agregar Dashboard</DialogTitle>
          <DialogDescription>
            Selecciona un dashboard predefinido para añadirlo a tu vista
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {PRESET_CATALOGUE.map(preset => {
            const Icon = preset.icon;
            const isActive = activePresets.includes(preset.id);
            return (
              <div
                key={preset.id}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                  isActive ? 'border-[#2d8659] bg-[#2d8659]/5' : 'border-border hover:border-[#1a365d]/40'
                }`}
              >
                <div className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-[#2d8659] text-white' : 'bg-muted text-muted-foreground'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{preset.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{preset.description}</p>
                </div>
                <Button
                  size="sm"
                  variant={isActive ? 'outline' : 'default'}
                  className={`shrink-0 text-xs h-7 ${!isActive ? 'bg-[#1a365d] hover:bg-[#1a365d]/90' : ''}`}
                  onClick={() => !isActive && onAdd(preset.id)}
                  disabled={isActive}
                >
                  {isActive ? 'Activo' : 'Agregar'}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">¿Necesitas un dashboard a medida?</p>
          <Button variant="outline" size="sm" onClick={handleRequest}>
            Solicitar dashboard personalizado
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────
export const DashboardPage = () => {
  const { user, isAdmin, isMaster, isAgente, isAsesor, isAspirante, isAsistente, isDeveloper } = useAuth();
  const { selectedLine, isAllLines, changeBusinessLine } = useBusinessLine();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leadsBySource, setLeadsBySource] = useState([]);
  const [leadsByStatus, setLeadsByStatus] = useState([]);
  const [presetDashboards, setPresetDashboards] = useState([]);
  const [agentesLista, setAgentesLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [convPoints, setConvPoints] = useState(null);
  const [convPanelOpen, setConvPanelOpen] = useState(true);
  const [convAgentId, setConvAgentId] = useState(null);
  const [ingresosYear, setIngresosYear] = useState(String(new Date().getFullYear()));
  const [ingresosYears, setIngresosYears] = useState([]);
  const [ingresosData, setIngresosData] = useState(null);
  const [ingresosAseguradora, setIngresosAseguradora] = useState('allianz');
  const [topEstelar, setTopEstelar] = useState(null);
  const [topVip, setTopVip] = useState(null);
  const [topEstelarOpen, setTopEstelarOpen] = useState(true);
  const [topVipOpen, setTopVipOpen] = useState(true);
  const [top5InsigniaAgents, setTop5InsigniaAgents] = useState([]);
  const [polizasDistrib, setPolizasDistrib] = useState([]);

  const COLORS = ['#1a365d', '#2d8659', '#6b7280', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#a855f7'];
  const aseguradoraLabel = isAllLines ? 'Todas las aseguradoras'
    : selectedLine === 'allianz'  ? 'Allianz'
    : selectedLine === 'insignia' ? 'Insignia Life'
    : selectedLine === 'qualitas' ? 'Quálitas'
    : selectedLine;

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Auto-seleccionar primera aseguradora del agente/aspirante al montar el dashboard
  useEffect(() => {
    if (!user?.id || !(isAgente || isAspirante)) return;
    const ASEG_MAP = {
      'Allianz': 'allianz',
      'Insignia Life': 'insignia',
      'Quálitas': 'qualitas',
      'Qualitas': 'qualitas',
    };
    const aseguradoras = user.aseguradoras || [];
    if (aseguradoras.length > 0) {
      const line = ASEG_MAP[aseguradoras[0]];
      if (line) changeBusinessLine(line);
      return;
    }
    // Para aspirante (aseguradoras no vienen en user object), fetch desde /api/agents
    axios.get(`${API}/agents`)
      .then(res => {
        const my = (res.data || []).find(a => a.user_id === user.id || a.user?.id === user.id);
        if (my?.aseguradoras?.length > 0) {
          const line = ASEG_MAP[my.aseguradoras[0]];
          if (line) changeBusinessLine(line);
        }
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    try {
      const chartParams = isAllLines ? '' : `?business_line=${selectedLine}`;
      const statsParams = isAllLines ? '' : `?business_line=${selectedLine}`;
      const [statsRes, sourceRes, statusRes, dashboardsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats${statsParams}`),
        axios.get(`${API}/reports/leads-by-source${chartParams}`),
        axios.get(`${API}/reports/leads-by-status${chartParams}`),
        axios.get(`${API}/custom-dashboards`),
      ]);
      setStats(statsRes.data);
      setLeadsBySource(sourceRes.data);
      setLeadsByStatus(statusRes.data);
      // Only keep preset type, sorted by config.sort_order
      const presets = dashboardsRes.data
        .filter(d => d.type === 'preset')
        .sort((a, b) => (a.config?.sort_order ?? 999) - (b.config?.sort_order ?? 999));
      setPresetDashboards(presets);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLine, isAllLines]);

  const fetchAgentes = useCallback(async () => {
    if (!isAdmin && !isMaster && !isAsistente) return;
    try {
      const res = await axios.get(`${API}/dashboard/agentes-lista`);
      setAgentesLista(res.data || []);
    } catch { /* ignore */ }
  }, [isAdmin, isMaster, isAsistente]);

  useEffect(() => {
    fetchData();
    fetchAgentes();
  }, [fetchData, fetchAgentes]);

  // Convention points — visible for agente, asesor, admin, master, developer, asistente
  useEffect(() => {
    const showConv = isAdmin || isMaster || isAgente || isAsesor || isAspirante || isDeveloper || isAsistente;
    if (!showConv || !user?.id) return;
    const targetId = convAgentId || user.id;
    axios.get(`${API}/agents/${targetId}/convention-points`)
      .then(r => setConvPoints(r.data))
      .catch(() => setConvPoints(null));
  }, [user, isAdmin, isMaster, isAgente, isAsesor, isAspirante, isDeveloper, isAsistente, convAgentId]);

  // Ingresos — admin only
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/dashboard/ingresos/available-years`)
      .then(r => setIngresosYears(r.data.years || []))
      .catch(() => {});
  }, [isAdmin]);

  // Sync ingresosAseguradora with global business line selector
  useEffect(() => {
    if (isAllLines) {
      setIngresosAseguradora('todas');
    } else if (['allianz', 'insignia', 'qualitas'].includes(selectedLine)) {
      setIngresosAseguradora(selectedLine);
    }
  }, [selectedLine, isAllLines]);

  useEffect(() => {
    if (!isAdmin) return;
    const params = new URLSearchParams({ page: 1, page_size: 1, start_year: ingresosYear });
    if (ingresosAseguradora !== 'todas') params.set('business_line', ingresosAseguradora);
    axios.get(`${API}/policies?${params}`)
      .then(r => setIngresosData({ total: r.data.total_acumulado ?? 0, polizas_count: r.data.total ?? 0 }))
      .catch(() => {});
  }, [isAdmin, ingresosYear, ingresosAseguradora]);

  // Distribución de pólizas por producto — todos los roles
  useEffect(() => {
    const params = new URLSearchParams();
    if (!isAllLines) params.set('aseguradora', selectedLine);
    axios.get(`${API}/policies/distribution?${params}`)
      .then(r => setPolizasDistrib(r.data.items || []))
      .catch(() => {});
  }, [selectedLine, isAllLines]);

  // Top 5 agentes — admin only, fetch both metas in parallel
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      axios.get(`${API}/dashboard/top-agents-convention?meta=estelar`),
      axios.get(`${API}/dashboard/top-agents-convention?meta=vip`),
    ]).then(([re, rv]) => {
      setTopEstelar(re.data);
      setTopVip(rv.data);
    }).catch(() => {});
  }, [isAdmin]);

  // Top 5 Insignia Life por prima — admin only
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/agents`).then(async (agentsRes) => {
      const agentsList = (agentsRes.data || [])
        .filter(a => a.aseguradoras?.some(s => s === 'Insignia Life' || s?.includes('Insignia')));
      const results = await Promise.all(
        agentsList.map(async (agent) => {
          try {
            const pts = await axios.get(`${API}/agents/${agent.user_id}/convention-points`);
            return {
              name:  agent.user?.full_name || '—',
              prima: pts.data?.prima_insignia || 0,
              nivel: pts.data?.nivel_convencion || null,
            };
          } catch { return { name: agent.user?.full_name || '—', prima: 0, nivel: null }; }
        })
      );
      setTop5InsigniaAgents(
        results.sort((a, b) => b.prima - a.prima).slice(0, 5)
      );
    }).catch(() => {});
  }, [isAdmin]);

  // ── DnD reorder ──
  const handleDragStart = useCallback(({ active }) => {
    setActiveDragId(String(active.id));
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = presetDashboards.findIndex(d => String(d.id) === String(active.id));
    const newIndex = presetDashboards.findIndex(d => String(d.id) === String(over.id));
    const reordered = arrayMove(presetDashboards, oldIndex, newIndex);

    // Optimistic update
    setPresetDashboards(reordered);

    // Persist
    try {
      await axios.patch(`${API}/custom-dashboards/reorder`,
        reordered.map((d, i) => ({ id: d.id, sort_order: i }))
      );
    } catch {
      toast.error('Error al guardar orden');
      fetchData(); // rollback
    }
  }, [presetDashboards, fetchData]);

  // ── Add / remove presets ──
  const handleAddPreset = async (presetId) => {
    const nextOrder = presetDashboards.length;
    try {
      await axios.post(`${API}/custom-dashboards`, {
        name: PRESET_CATALOGUE.find(p => p.id === presetId)?.title || presetId,
        type: 'preset',
        data_source: presetId,
        config: { preset_type: presetId, sort_order: nextOrder },
      });
      toast.success('Dashboard agregado');
      fetchData();
    } catch {
      toast.error('Error al agregar dashboard');
    }
  };

  const handleRemovePreset = async (dashboardId) => {
    try {
      await axios.delete(`${API}/custom-dashboards/${dashboardId}`);
      toast.success('Dashboard eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const activePresetIds = presetDashboards.map(d => d.config?.preset_type || d.data_source);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Bienvenido, {user?.full_name}. Aquí está el resumen de hoy.
          </p>
        </div>
        {isAdmin && (
          <Button
            className="bg-[#2d8659] hover:bg-[#2d8659]/90"
            onClick={() => setGalleryOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Dashboard
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      {/* La 3ra card (Ingresos) no se renderiza para admin cuando SHOW_INGRESOS_CARD
          es false (ver más abajo) — en ese caso el grid pasa a 2 columnas para que
          las 2 cards restantes llenen el ancho. Para no-admin siempre hay 3 cards
          (StatCard de Ingresos no depende de esa bandera), así que se mantiene lg:grid-cols-3. */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${isAdmin && !SHOW_INGRESOS_CARD ? '' : 'lg:grid-cols-3'}`}>
        <StatCard
          title="Total Leads"
          value={stats?.total_leads || 0}
          description={`${stats?.leads_this_month || 0} este mes`}
          icon={Target}
          trend="+12% vs mes anterior"
          trendUp={true}
          color="primary"
        />
        <StatCard
          title="Clientes Activos"
          value={stats?.total_clients || 0}
          description={`${stats?.conversions_this_month || 0} conversiones`}
          icon={Users}
          trend="+8% vs mes anterior"
          trendUp={true}
          color="secondary"
        />
        {isAdmin ? (
          SHOW_INGRESOS_CARD && (
          <Card className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-[#1a365d] text-white">
                    <DollarSign className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">Ingresos</p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Select value={ingresosYear} onValueChange={setIngresosYear}>
                    <SelectTrigger className="h-7 text-xs w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas (histórico)</SelectItem>
                      {ingresosYears.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={ingresosAseguradora} onValueChange={setIngresosAseguradora}>
                    <SelectTrigger className="h-7 text-xs w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas</SelectItem>
                      <SelectItem value="allianz">Allianz</SelectItem>
                      <SelectItem value="insignia">Insignia Life</SelectItem>
                      <SelectItem value="qualitas">Quálitas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between font-bold text-base">
                  <span>Total acumulado</span>
                  <span>{formatCurrency(ingresosData?.total ?? 0)}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {ingresosData?.polizas_count ?? 0} pólizas en este periodo
              </p>
            </CardContent>
          </Card>
          )
        ) : (
          <StatCard
            title="Ingresos"
            value={formatCurrency(stats?.total_income || 0)}
            description="Total cobrado"
            icon={DollarSign}
            trend="+15% vs mes anterior"
            trendUp={true}
            color="primary"
          />
        )}
      </div>

      {/* Convención Allianz */}
      {(isAllLines || selectedLine === 'allianz') && (isAdmin || isMaster || isAgente || isAsesor || isAspirante || isDeveloper || isAsistente) && convPoints !== null && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setConvPanelOpen(o => !o)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 text-white">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {/* TODO: cambiar destinos VIP/Estelar cada año según material oficial de Allianz */}
                    Mi camino a la convención {new Date().getFullYear()}
                    {convPoints.estelar_qualified && (
                      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs">✓ Estelar</Badge>
                    )}
                    {!convPoints.estelar_qualified && convPoints.vip_qualified && (
                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">✓ VIP</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">Pólizas {aseguradoraLabel} activas {new Date().getFullYear()}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(isAdmin || isMaster || isAsistente) && agentesLista.length > 0 && (
                  <div onClick={e => e.stopPropagation()}>
                    <Select
                      value={convAgentId || '__self__'}
                      onValueChange={v => setConvAgentId(v === '__self__' ? null : v)}
                    >
                      <SelectTrigger className="h-7 text-xs w-44">
                        <SelectValue placeholder="Mi cuenta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__self__">Mi cuenta</SelectItem>
                        {agentesLista.map(a => (
                          <SelectItem key={a.user_id} value={a.user_id}>{a.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">{convPoints.total_points} pts</span>
                {convPanelOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          </CardHeader>
          {convPanelOpen && (
            <CardContent className="pt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">VIP — Huatulco</span>
                    <span>{convPoints.total_points} / 120 pts {convPoints.vip_qualified && <span className="text-green-600">✓</span>}</span>
                  </div>
                  <Progress value={Math.min((convPoints.total_points / 120) * 100, 100)} className="h-2" />
                  {!convPoints.vip_qualified && <p className="text-xs text-muted-foreground">Faltan {Math.max(120 - convPoints.total_points, 0).toFixed(1)} pts</p>}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">Estelar — Marruecos</span>
                    <span>{convPoints.total_points} / 180 pts {convPoints.estelar_qualified && <span className="text-green-600">✓</span>}</span>
                  </div>
                  <Progress value={Math.min((convPoints.total_points / 180) * 100, 100)} className="h-2" />
                  {!convPoints.estelar_qualified && <p className="text-xs text-muted-foreground">Faltan {Math.max(180 - convPoints.total_points, 0).toFixed(1)} pts</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                {Object.entries(convPoints.breakdown).map(([key, val]) => (
                  <div key={key} className="bg-muted/50 rounded-lg p-2">
                    <div className="text-lg font-bold">{val}</div>
                    <div className="text-xs text-muted-foreground">
                      {{'ahorro':'Ahorro','proteccion':'Protección','gmmi':'GMMI','autos':'Autos','residencial':'Residencial','inversion':'Inversión','danos':'Daños','beneficios':'Beneficios'}[key] || key}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Trazando tu camino — Insignia Life */}
      {(isAllLines || selectedLine === 'insignia') && (isAdmin || isMaster || isAgente || isAsesor || isAspirante || isDeveloper || isAsistente) && convPoints !== null && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Trazando tu camino — Insignia Life
                  {(convPoints.prima_insignia || 0) >= 2660000 && (
                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs">✈️ Lisboa</Badge>
                  )}
                  {(convPoints.prima_insignia || 0) >= 1150000 && (convPoints.prima_insignia || 0) < 2660000 && (
                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs">🏖️ Cancún</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">Prima acumulada Insignia Life {new Date().getFullYear()}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${(convPoints.prima_insignia || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">Prima computable acumulada</p>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium">🏖️ Nacional — Cancún 2027</span>
                  <span className="text-muted-foreground">
                    ${(convPoints.prima_insignia || 0).toLocaleString('es-MX')} / $1,150,000
                  </span>
                </div>
                <InsigniaProgressWithMarkers currentPrima={convPoints.prima_insignia || 0} meta={1150000} barColor="#a855f7" currentNivel={convPoints.nivel_convencion} medalasStatus={convPoints.medallas_status || {}} />
                {(convPoints.prima_insignia || 0) < 1150000 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Faltan ${Math.max(0, 1150000 - (convPoints.prima_insignia || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium">✈️ Internacional — Lisboa 2027</span>
                  <span className="text-muted-foreground">
                    ${(convPoints.prima_insignia || 0).toLocaleString('es-MX')} / $2,660,000
                  </span>
                </div>
                <InsigniaProgressWithMarkers currentPrima={convPoints.prima_insignia || 0} meta={2660000} barColor="#7e22ce" currentNivel={convPoints.nivel_convencion} medalasStatus={convPoints.medallas_status || {}} />
                {(convPoints.prima_insignia || 0) < 2660000 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Faltan ${Math.max(0, 2660000 - (convPoints.prima_insignia || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
            </div>

            {/* Tu nivel actual */}
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Tu nivel actual</p>
              {(() => {
                const nivel = convPoints.nivel_convencion;
                const cfg = nivel && INSIGNIA_MEDAL_CONFIG[nivel];
                const medalasStatus = convPoints.medallas_status || {};
                const caducadas = Object.values(medalasStatus).filter(v => v.status === 'caducada').length;
                return (
                  <div className="flex flex-col items-center gap-2 py-1">
                    <InsigniaMedalSVG nivel={nivel} size={80} />
                    <div className="text-center">
                      <p className="text-base font-bold" style={{ color: cfg ? cfg.color : '#9CA3AF' }}>
                        {cfg ? cfg.label : 'Sin nivel aún'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {cfg ? cfg.text : '¡Comienza tu camino hacia el primer nivel!'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {convPoints.polizas_insignia || 0} pólizas · ${(convPoints.prima_insignia || 0).toLocaleString('es-MX')}
                      </p>
                      {caducadas > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          ⚠️ {caducadas} medalla{caducadas > 1 ? 's' : ''} ya no disponible{caducadas > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top 5 Insignia Life por prima — solo admin */}
      {isAdmin && (isAllLines || selectedLine === 'insignia') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="h-4 w-4 text-purple-500" />
                🏖️ Top 5 → Nacional (Cancún 2027)
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DC143C', color: '#fff' }}>Insignia Life</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {top5InsigniaAgents.map((agent, i) => {
                const pctCanc = Math.min(100, (agent.prima / 1150000) * 100);
                const primaK = agent.prima >= 1000000
                  ? `$${(agent.prima / 1000000).toFixed(2)}M`
                  : agent.prima >= 1000
                  ? `$${Math.round(agent.prima / 1000)}K`
                  : `$${agent.prima}`;
                return (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-xs text-muted-foreground font-bold">{i + 1}º</span>
                    <span className="flex-1 truncate" title={agent.name}>{agent.name.length > 18 ? agent.name.slice(0, 18) + '…' : agent.name}</span>
                    {agent.nivel && INSIGNIA_MEDAL_CONFIG[agent.nivel] && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: INSIGNIA_MEDAL_CONFIG[agent.nivel].color, color: INSIGNIA_MEDAL_CONFIG[agent.nivel].textColor }}>
                        {INSIGNIA_MEDAL_CONFIG[agent.nivel].label}
                      </span>
                    )}
                    <div className="w-14 bg-muted rounded-full h-1.5 shrink-0">
                      <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${pctCanc}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{primaK} / $1.15M</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="h-4 w-4 text-purple-700" />
                ✈️ Top 5 → Internacional (Lisboa 2027)
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DC143C', color: '#fff' }}>Insignia Life</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {top5InsigniaAgents.map((agent, i) => {
                const pctLisb = Math.min(100, (agent.prima / 2660000) * 100);
                const primaK = agent.prima >= 1000000
                  ? `$${(agent.prima / 1000000).toFixed(2)}M`
                  : agent.prima >= 1000
                  ? `$${Math.round(agent.prima / 1000)}K`
                  : `$${agent.prima}`;
                return (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-xs text-muted-foreground font-bold">{i + 1}º</span>
                    <span className="flex-1 truncate" title={agent.name}>{agent.name.length > 18 ? agent.name.slice(0, 18) + '…' : agent.name}</span>
                    {agent.nivel && INSIGNIA_MEDAL_CONFIG[agent.nivel] && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: INSIGNIA_MEDAL_CONFIG[agent.nivel].color, color: INSIGNIA_MEDAL_CONFIG[agent.nivel].textColor }}>
                        {INSIGNIA_MEDAL_CONFIG[agent.nivel].label}
                      </span>
                    )}
                    <div className="w-14 bg-muted rounded-full h-1.5 shrink-0">
                      <div className="bg-purple-700 h-1.5 rounded-full" style={{ width: `${pctLisb}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{primaK} / $2.66M</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top 5 Agentes — solo admin */}
      {isAdmin && (isAllLines || selectedLine === 'allianz') && (topEstelar || topVip) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top 5 → VIP */}
          {topVip && (
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader
                className="pb-2 cursor-pointer select-none"
                onClick={() => setTopVipOpen(o => !o)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-blue-500" />
                    Top 5 → VIP (Huatulco)
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#2e8de0', color: '#fff' }}>Allianz</span>
                  </CardTitle>
                  {topVipOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </CardHeader>
              {topVipOpen && (
                <CardContent className="pt-0 space-y-2">
                  {topVip.agents.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Aún no hay agentes con puntos en este año</p>
                  ) : topVip.agents.map((a, i) => (
                    <div key={a.user_id} className="flex items-center gap-2 text-sm">
                      <span className="w-5 text-xs text-muted-foreground font-bold">{i + 1}º</span>
                      <span className="flex-1 truncate" title={a.name}>{a.name.length > 30 ? a.name.slice(0, 30) + '…' : a.name}</span>
                      <span className="text-xs text-muted-foreground w-20 text-right">{a.puntos} / {topVip.umbral}</span>
                      <div className="w-20 bg-muted rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full"
                          style={{ width: `${Math.min(a.pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs w-10 text-right">{a.pct}%</span>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Top 5 → Estelar */}
          {topEstelar && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader
                className="pb-2 cursor-pointer select-none"
                onClick={() => setTopEstelarOpen(o => !o)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-500" />
                    Top 5 → Estelar (Marruecos)
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#2e8de0', color: '#fff' }}>Allianz</span>
                  </CardTitle>
                  {topEstelarOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </CardHeader>
              {topEstelarOpen && (
                <CardContent className="pt-0 space-y-2">
                  {topEstelar.agents.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Aún no hay agentes con puntos en este año</p>
                  ) : topEstelar.agents.map((a, i) => (
                    <div key={a.user_id} className="flex items-center gap-2 text-sm">
                      <span className="w-5 text-xs text-muted-foreground font-bold">{i + 1}º</span>
                      <span className="flex-1 truncate" title={a.name}>{a.name.length > 30 ? a.name.slice(0, 30) + '…' : a.name}</span>
                      <span className="text-xs text-muted-foreground w-20 text-right">{a.puntos} / {topEstelar.umbral}</span>
                      <div className="w-20 bg-muted rounded-full h-1.5">
                        <div
                          className="bg-amber-500 h-1.5 rounded-full"
                          style={{ width: `${Math.min(a.pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs w-10 text-right">{a.pct}%</span>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Mi Dashboard (non-admin) */}
      {!isAdmin && <MiDashboard />}

      {/* Preset Dashboards — drag & drop */}
      {isAdmin && presetDashboards.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">Mis Dashboards</h2>
            <span className="text-xs text-muted-foreground">(arrastra para reordenar)</span>
          </div>
          {/* Wrapper con cursor persistente durante drag */}
          <div style={{ cursor: activeDragId ? 'grabbing' : 'grab' }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={presetDashboards.map(d => String(d.id))} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {presetDashboards.map(d => (
                    <PresetDashboardCard
                      key={d.id}
                      presetId={d.config?.preset_type || d.data_source}
                      dashboardDbId={d.id}
                      onRemove={handleRemovePreset}
                      agentesLista={agentesLista}
                      externalAseguradora={isAllLines ? '' : selectedLine}
                    />
                  ))}
                </div>
              </SortableContext>

              {/* DragOverlay — clon flotante con cursor grabbing garantizado */}
              <DragOverlay dropAnimation={null}>
                {activeDragId ? (() => {
                  const db = presetDashboards.find(d => String(d.id) === activeDragId);
                  const meta = db
                    ? PRESET_CATALOGUE.find(p => p.id === (db.config?.preset_type || db.data_source))
                    : null;
                  if (!meta) return null;
                  const MetaIcon = meta.icon;
                  return (
                    <div style={{ cursor: 'grabbing', opacity: 0.9 }}>
                      <Card className="shadow-2xl">
                        <CardHeader className="pb-2">
                          <div className="flex items-center gap-1.5">
                            <GripVertical className="h-4 w-4 text-muted-foreground/40" />
                            <MetaIcon className="h-4 w-4 text-muted-foreground" />
                            <CardTitle className="text-base">{meta.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                          Reordenando…
                        </CardContent>
                      </Card>
                    </div>
                  );
                })() : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6">
        {/* Distribución de pólizas por producto */}
        {polizasDistrib.length > 0 && (
          <Card className="max-w-2xl">
            <Accordion type="single" collapsible>
              <AccordionItem value="distribucion-producto" className="border-none">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex flex-col space-y-1.5 text-left">
                    <CardTitle>Distribución por producto</CardTitle>
                    <CardDescription>Pólizas según tipo de producto</CardDescription>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-[160px] h-[160px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={polizasDistrib}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="count"
                            nameKey="producto"
                            onClick={(entry) => navigate(`/policies?producto=${encodeURIComponent(entry.producto)}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {polizasDistrib.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, n) => {
                            const total = polizasDistrib.reduce((s, d) => s + d.count, 0);
                            return [`${v} (${total > 0 ? ((v / total) * 100).toFixed(0) : 0}%)`, n];
                          }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-1 overflow-y-auto max-h-[160px]">
                      {polizasDistrib.map((item, index) => (
                        <div key={item.producto} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                            <span className="truncate max-w-[160px]">{item.producto}</span>
                          </div>
                          <span className="font-medium text-sm ml-2">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
        )}
      </div>

      {/* Pipeline */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Estado del Pipeline</CardTitle>
            <CardDescription>Distribución de leads por estado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsByStatus} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" className="text-xs" />
                  <YAxis dataKey="status" type="category" width={130} className="text-xs" tickFormatter={normalizeStatus} />
                  <Tooltip formatter={(value, name, props) => [value, normalizeStatus(props.payload?.status || name)]} />
                  <Bar dataKey="count" fill="#1a365d" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Gallery Modal */}
      <GalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        activePresets={activePresetIds}
        onAdd={async (presetId) => {
          await handleAddPreset(presetId);
          setGalleryOpen(false);
        }}
      />
    </div>
  );
};
